import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, FileText } from "lucide-react";
import { MarketingNav, MarketingFooter, PipelineBar } from "@/components/marketing/MarketingChrome";
import { getUseCase, USE_CASES } from "@/data/useCases";
import { Seo } from "@/components/Seo";

export default function UseCaseDetail() {
  const { slug } = useParams();
  const uc = getUseCase(slug);
  if (!uc) return <Navigate to="/use-cases" replace />;
  const others = USE_CASES.filter((u) => u.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Seo
        title={`Make ${uc.label} with Espresso`}
        description={`${uc.tagline} ${uc.excerpt}`}
        image={uc.og}
        path={`/use-cases/${uc.slug}`}
      />
      <MarketingNav />

      <section className="border-b border-border grid-lines">
        <div className="mx-auto max-w-6xl px-6 py-14 lg:py-20">
          <Link to="/use-cases" className="mb-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-primary" data-testid="back-to-usecases">
            <ArrowLeft size={13} /> Output Catalogue
          </Link>
          <div className="mb-6 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            <span className="text-primary">{uc.ref}</span>
            <span className="h-px flex-1 bg-border" />
            <span>{uc.category}</span>
          </div>
          <h1 className="max-w-4xl font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="usecase-title">
            {uc.label}
          </h1>
          <p className="mt-5 max-w-2xl font-display text-xl text-foreground">{uc.tagline}</p>
          <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">{uc.excerpt}</p>
          <div className="mt-8">
            <Link to="/register">
              <Button size="lg" className="group rounded-sm px-8" data-testid="usecase-cta-button">
                {uc.cta}
                <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pipeline */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 py-14">
          <div className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-primary">The Processing Pipeline</div>
          <PipelineBar steps={uc.pipeline} />
        </div>
      </section>

      {/* Example project mock */}
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-6 py-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Example Project</div>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight">Watch a {uc.kind.toLowerCase()} take shape.</h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              Enter a subject and Espresso begins by decomposing it into a structured knowledge blueprint —
              then processes every node, validates it, and synthesises the finished, publication-ready output.
            </p>
            <div className="mt-6">
              <Link to="/register">
                <Button variant="outline" className="rounded-sm" data-testid="usecase-example-cta">
                  {uc.cta} <ArrowRight size={15} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="lg:col-span-7"
            data-testid="example-project-preview"
          >
            <div className="border border-border bg-card">
              <div className="flex items-center justify-between border-b border-border px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <span>Espresso / Projects / {uc.example.title}</span>
                <span>{uc.example.meta}</span>
              </div>
              <div className="p-5">
                <div className="font-display text-2xl font-semibold leading-tight tracking-tight">{uc.example.title}</div>
                <div className="mt-4 space-y-1">
                  {uc.example.chapters.map((c, i) => (
                    <div key={c} className="flex items-center gap-3 py-1.5 text-sm" style={{ paddingLeft: (i % 3 === 0 ? 0 : 18) }}>
                      <FileText size={14} className="text-muted-foreground" />
                      <span className={i % 3 === 0 ? "font-medium" : "text-muted-foreground"}>{c}</span>
                      <span className={`ml-auto h-2 w-2 rounded-full ${i < 2 ? "bg-[hsl(var(--processor-success))]" : i < 4 ? "bg-[hsl(var(--processor-active))] animate-pulse" : "bg-muted-foreground/40"}`} />
                    </div>
                  ))}
                </div>
                <div className="mt-5 border-t border-border pt-4 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Ready to publish
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* More outputs */}
      <section className="mx-auto max-w-6xl px-6 py-14">
        <div className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">More Outputs</div>
        <div className="grid grid-cols-1 gap-px overflow-hidden rounded-sm border border-border bg-border sm:grid-cols-3">
          {others.map((u) => (
            <Link key={u.slug} to={`/use-cases/${u.slug}`} className="group bg-card p-6 transition-colors hover:bg-secondary" data-testid="use-case-specimen-card">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{u.ref}</div>
              <h3 className="mt-4 font-display text-2xl font-semibold leading-tight tracking-tight">{u.label}</h3>
              <span className="mt-4 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-primary">
                Open <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
